import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";

import { BrandCard } from "~/components/BrandCard";
import { ErrorComponent } from "~/components/error-boundary";
import { PageContainer } from "~/components/ui/layout/page-container";
import { Wrapper } from "~/components/ui/layout/wrapper";
import { PageHeading } from "~/components/ui/page-heading";
import { Heading } from "~/components/ui/typography";
import { sizingToolData } from "~/config/tools-and-services";
import { getBrands } from "~/models/brand.server";
import { AppError } from "~/utils/errors";
import { getSeoMeta } from "~/utils/seo";

export const meta: MetaFunction = (metaArgs) => {
  return getSeoMeta(metaArgs, {
    title: "Outils de dimensionnement - Outils et Services",
    description:
      "Découvrez les outils de dimensionnement de chaque marque pour vos projets photovoltaïques.",
  });
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  try {
    const brands = await getBrands({ page: 1, itemsPerPage: 300, request });
    return json({
      brands,
      errors: null,
    });
  } catch (error) {
    if (error instanceof AppError) {
      return json({
        brands: null,
        errors: error.getAllErrors(),
      });
    }
    throw error;
  }
};

const SizingToolPage = () => {
  const { brands } = useLoaderData<typeof loader>();

  const breadcrumbData = [
    {
      name: "Outils et Services",
      slug: "/outils-et-services",
    },
    {
      name: "Outils de dimensionnement",
      slug: "",
    },
  ];

  const sizingToolCategories = sizingToolData.map((sizingToolCategory) => {
    const tools = sizingToolCategory.tools.map((tool) => {
      if (tool.brandSlug) {
        const brand = brands?.find((brand) => brand.slug === tool.brandSlug) || null;
        return {
          key: `${brand?.id}-${brand?.slug}`,
          title: brand?.title ?? brand?.slug ?? tool?.brandSlug,
          image: brand?.images[0]?.url,
          url: tool.url,
        };
      }
      return {
        key: tool.title,
        title: tool.title,
        image: tool.image,
        url: tool.url,
      };
    });

    return {
      title: sizingToolCategory.title,
      description: sizingToolCategory.description,
      tools,
    };
  });

  return (
    <PageContainer>
      <PageHeading
        breadcrumbData={breadcrumbData}
        headingText="Outils de dimensionnement"
        description="Découvrez les outils de dimensionnement de chaque marque pour vos projets photovoltaïques."
      />
      <Wrapper>
        {sizingToolCategories.map((sizingToolCategory) => (
          <>
            <Heading as="h2" className="mb-2">
              {sizingToolCategory.title}
            </Heading>
            <p className="mb-8 font-medium leading-normal text-grey-500">
              {sizingToolCategory.description}
            </p>
            <div className="mb-12 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {sizingToolCategory.tools.map((tool) => (
                <BrandCard
                  key={tool.key}
                  image={tool.image ?? null}
                  name={tool.title ?? ""}
                  slug={tool.url}
                  newTab={tool.url.startsWith("http")}
                />
              ))}
            </div>
          </>
        ))}
      </Wrapper>
    </PageContainer>
  );
};

export default SizingToolPage;

export const ErrorBoundary = () => {
  return <ErrorComponent />;
};
